// Mobile-only header summary of booking selections with inline Change links.
// Each Change resets that field (and dependents per reducer rules) and scrolls
// the host page to the corresponding strip field.

import { useEffect, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listResources } from '../api/client';
import { formatPillDate, formatSlotTime } from '../utils/datetime';
import { stripBookingFieldActions, type StripBookingField } from '../utils/bookingFlow';
import type { BookingAction, BookingState } from '../state/machine';

interface MobileSelectionSummaryProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>
}

interface SummaryRow {
  field: StripBookingField;
  value: string;
  trackId: string;
}

/** Scroll the host page so a strip field is near the top of the viewport. */
function scrollToStripBookingField(field: StripBookingField, root?: HTMLElement | null) {
  requestAnimationFrame(() => {
    const scope = root ?? document;
    const el = scope.querySelector(`[data-booking-mobile-field="${field}"]`) as HTMLElement | null;
    if (!el) return;
    const targetY = window.scrollY + el.getBoundingClientRect().top - 20;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  });
}

export function MobileSelectionSummary({ state, dispatch }: MobileSelectionSummaryProps) {
  const [fitterName, setFitterName] = useState<string | null>(null);


  const handleMobileFieldChange = (field: StripBookingField) => {
    for (const action of stripBookingFieldActions(field)) {
      dispatch(action);
    }
    scrollToStripBookingField(field);
  };

  useEffect(() => {
    if (!state.selectedFitterId || !state.selectedLocation) {
      setFitterName(null);
      return;
    }

    let cancelled = false;
    listResources()
      .then((all) => {
        if (cancelled) return;
        const match = all.find((r) => r.id === state.selectedFitterId);
        setFitterName(match?.name ?? null);
      })
      .catch(() => {
        if (!cancelled) setFitterName(null);
      });

    return () => {
      cancelled = true;
    };
  }, [state.selectedFitterId, state.selectedLocation]);

  const rows: SummaryRow[] = [];

  if (state.selectedLocation) {
    rows.push({
      field: 'location',
      value: state.selectedLocation.name,
      trackId: 'change_location',
    });
  }

  if (state.selectedFittingType) {
    rows.push({
      field: 'fittingType',
      value: state.selectedFittingType.displayName ?? state.selectedFittingType.name,
      trackId: 'change_fitting_type',
    });
  }

  if (state.selectedDate) {
    rows.push({
      field: 'date',
      value: formatPillDate(state.selectedDate),
      trackId: 'change_date',
    });
  }

  const resolvedFitterName = fitterName ?? state.selectedSlot?.fitterName ?? null;
  if (resolvedFitterName) {
    rows.push({
      field: 'fitter',
      value: `With ${resolvedFitterName}`,
      trackId: 'change_fitter',
    });
  }

  if (state.selectedSlot) {
    rows.push({
      field: 'time',
      value: formatSlotTime(state.selectedSlot.start),
      trackId: 'change_time',
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className={styles.mobileSelectionSummary}>
      {rows.map((row) => (
        <div key={row.field} className={styles.mobileSelectionRow}>
          <span className={styles.mobileSelectionValue}>{row.value}</span>
          <button
            type="button"
            className={styles.mobileSelectionChange}
            data-cc-track="change_link"
            data-cc-track-id={row.trackId}
            onClick={() => handleMobileFieldChange(row.field)}
          >
            Change
          </button>
        </div>
      ))}
    </div>
  );
}
